// rea-import.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { BaseResponseModel } from 'src/app/models/base-response.model';
import {
  FincaReaRequest,
  HistoricoREAModel,
  ReaAuditData,
  ReaImportAuditDetailResponse,
  ReaImportAuditRecord,
  ReaImportAuditStats,
  ReaImportData,
} from 'src/app/models/fincas/rea.model';

@Injectable({ providedIn: 'root' })
export class ReaImportService {
  private readonly importEndpoint: string = `${environment.serverUrl}/ws/index.php?p1=fincas&p2=importREA`;
  private readonly lastRegisterEndpoint: string = `${environment.serverUrl}/ws/index.php?p1=fincas&p2=getLastRegisterREA`;
  private readonly auditHistoryEndpoint: string = `${environment.serverUrl}/ws/index.php?p1=fincas&p2=getReaImportAudit`;
  private readonly auditDetailEndpoint: string = `${environment.serverUrl}/ws/index.php?p1=fincas&p2=getReaImportAuditDetail`;
  private readonly auditStatsEndpoint: string = `${environment.serverUrl}/ws/index.php?p1=fincas&p2=getReaImportAuditStats`;

  constructor(private readonly http: HttpClient) {}

  public checkRea(payload: FincaReaRequest): Observable<BaseResponseModel<ReaImportData>> {
    return this.performReaRequest({ ...payload, operationType: 'check' });
  }

  public importRea(payload: FincaReaRequest): Observable<BaseResponseModel<ReaImportData>> {
    return this.performReaRequest({ ...payload, operationType: 'import' });
  }

  public getLastRegisterRea(codigoREA: string): Observable<BaseResponseModel<HistoricoREAModel>> {
    const params = new HttpParams().set('codigo_REA', codigoREA);

    return this.http.get<unknown>(this.lastRegisterEndpoint, { params }).pipe(
      map((resp) => BaseResponseModel.normalize<HistoricoREAModel>(resp)),
      catchError((error: unknown) => of(
        BaseResponseModel.fromError<HistoricoREAModel>(error, 'Error inesperado al consultar histórico REA'),
      )),
    );
  }

  public getImportAuditHistory(codigoREA: string, limit: number): Observable<BaseResponseModel<ReaImportAuditRecord>> {
    const params = new HttpParams()
      .set('codigo_REA', codigoREA)
      .set('limit', String(limit));

    return this.http.get<unknown>(this.auditHistoryEndpoint, { params }).pipe(
      map((resp) => BaseResponseModel.normalize<ReaImportAuditRecord>(resp)),
      catchError((error: unknown) => of(
        BaseResponseModel.fromError<ReaImportAuditRecord>(error, 'Error inesperado al consultar auditoría REA'),
      )),
    );
  }

  public getImportAuditDetail(importId: string): Observable<BaseResponseModel<ReaAuditData>> {
    const params = new HttpParams().set('importId', importId);

    return this.http.get<unknown>(this.auditDetailEndpoint, { params }).pipe(
      map((resp) => BaseResponseModel.normalize<ReaAuditData>(resp)),
      catchError((error: unknown) => of(BaseResponseModel.fromError<ReaAuditData>(error, 'Error inesperado al consultar detalle REA'))),
    );
  }

  public getImportAuditStats(codigoREA: string, limit: number): Observable<BaseResponseModel<ReaImportAuditStats>> {
    const params = new HttpParams()
      .set('codigo_REA', codigoREA)
      .set('limit', String(limit));

    return this.http.get<unknown>(this.auditStatsEndpoint, { params }).pipe(
      map((resp) => BaseResponseModel.normalize<ReaImportAuditStats>(resp)),
      catchError((error: unknown) => of(
        BaseResponseModel.fromError<ReaImportAuditStats>(error, 'Error inesperado al consultar estadísticas REA'),
      )),
    );
  }

  public normalizeAuditDetailResponse(value: ReaAuditData | null): ReaImportAuditDetailResponse | null {
    if (value == null || !('import' in value) || !('details' in value)) {
      return null;
    }

    const response = value as Partial<ReaImportAuditDetailResponse>;

    return response.import != null && Array.isArray(response.details)
      ? response as ReaImportAuditDetailResponse
      : null;
  }

  private performReaRequest(payload: FincaReaRequest): Observable<BaseResponseModel<ReaImportData>> {
    return this.http.post<unknown>(this.importEndpoint, payload).pipe(
      map((resp) => BaseResponseModel.normalize<ReaImportData>(resp)),
      catchError((error: unknown) => of(BaseResponseModel.fromError<ReaImportData>(error, 'Error inesperado al importar REA'))),
    );
  }
}
